import { Calendar, FileText, MessageSquare, Search, Users, type LucideIcon } from "lucide-react";
import {
  canSeeRegistration,
  chatNavItem,
  eventDetailsNavItem,
  eventUsersNavItem,
  eventsNavItem,
  homeNavItem,
  notesNavItem,
  organizationItems,
  sourceGroup,
} from "@/features/enterprise-sidebar/_logic/navigation";
import type { NavItem } from "@/features/enterprise-sidebar/_logic/types";
import { ENTERPRISE_REGISTRATION_TABS, registrationTabHref } from "@/lib/routes/registration-paths";
import { EVENT_BUCKET_ORDER, type EventBucket } from "@/lib/events/event-lifecycle";
import { KEYWORDS_BY_ID, KEYWORDS_BY_LEAF } from "./keywords";
import type { PaletteGroup, PaletteItem } from "./types";

/** Row tags, matching the event switcher's own labels. */
const BUCKET_LABEL: Record<EventBucket, string> = { active: "", past: "Past", cancelled: "Cancelled" };

/** Last path segment, used to look up leaf synonyms for settings/reporting children. */
const leafOf = (href: string) => href.split("?")[0].split("/").filter(Boolean).pop() ?? "";

/** Synonyms for a nav item: its own id first, then its route's leaf. */
const keywordsFor = (item: NavItem): string[] => [
  ...(KEYWORDS_BY_ID[item.id] ?? []),
  ...(KEYWORDS_BY_LEAF[leafOf(item.href)] ?? []),
];

/**
 * Converts one sidebar nav item into a palette item.
 *
 * `comingSoon` becomes a `requires` predicate rather than an exclusion, so a
 * gated capability still appears (disabled) and the user learns it exists —
 * silently hiding features is the problem this whole project exists to fix.
 */
const fromNavItem = (item: NavItem, group: PaletteGroup, scope: PaletteItem["scope"]): PaletteItem => ({
  id: item.id,
  label: item.label,
  group,
  icon: item.icon,
  keywords: keywordsFor(item),
  scope,
  run: { kind: "navigate", href: item.href },
  ...(item.comingSoon ? { requires: (context) => context.isNowadaysAdmin, disabledReason: "Coming soon" } : {}),
});

/**
 * Flattens a nav item's `children` into their own items.
 *
 * Labels stay plain. An earlier version prefixed them ("Settings: Profile")
 * because every group shared one heading; giving Settings its own heading does
 * that job better and without the prefix noise.
 */
const fromNavChildren = (item: NavItem, group: PaletteGroup, scope: PaletteItem["scope"]): PaletteItem[] =>
  (item.children ?? []).map((child) => ({
    ...fromNavItem(child, group, scope),
    id: `${item.id}-${child.id}`,
  }));

/**
 * Sourcing destinations that exist as routes but have no sidebar entry, so they
 * cannot be derived.
 *
 * Compare is deliberately not here. It lives in the quick actions instead, and
 * listing it in both put two rows reading "Compare proposals..." on screen for
 * any query matching either one, the same unreadable duplicate the Users
 * relabel below exists to prevent.
 */
const REPORTING_PAGES: { slug: string; label: string }[] = [
  // Named rather than plain: Registration has an "Overview" too, and two rows
  // reading "Overview" tell you nothing about which one you are picking.
  { slug: "overview", label: "Reporting overview" },
  { slug: "spend", label: "Spend" },
  { slug: "savings", label: "Savings" },
  { slug: "duty-of-care", label: "Duty of care" },
  { slug: "sustainability", label: "Sustainability" },
  { slug: "user-satisfaction", label: "User satisfaction" },
  { slug: "tool-usage", label: "Tool usage" },
];

/**
 * Reporting's individual pages. The sidebar's Reporting item has no `children`,
 * so unlike Settings these cannot be derived — but they are real routes and
 * people reach them, so leaving them out would reproduce the gap this feature
 * exists to close.
 */
const reportingItems = (): PaletteItem[] =>
  REPORTING_PAGES.map(({ slug, label }) => ({
    id: `reporting-${slug}`,
    label,
    group: "reporting" as const,
    icon: FileText,
    keywords: [...(KEYWORDS_BY_ID.reporting ?? []), ...(KEYWORDS_BY_LEAF[slug] ?? [])],
    scope: "org" as const,
    run: { kind: "navigate" as const, href: `/dashboard/organization/reporting/${slug}` },
  }));

const UNDERIVABLE_ITEMS: PaletteItem[] = [
  {
    id: "search-restaurant",
    label: "Search restaurants",
    group: "source",
    icon: Search,
    keywords: KEYWORDS_BY_ID.restaurant,
    scope: "event",
    run: { kind: "navigate", href: "/dashboard/source/{eventId}/search/restaurant" },
  },
  {
    id: "search-event-space",
    label: "Search event spaces",
    group: "source",
    icon: Search,
    keywords: KEYWORDS_BY_ID.event_space,
    scope: "event",
    run: { kind: "navigate", href: "/dashboard/source/{eventId}/search/event_space" },
  },
];

/**
 * Settings children whose sidebar label collides with an event-level row.
 *
 * The sidebar can call both "Contracts" because they sit under different
 * headings a click apart; the palette shows every group at once, so a query for
 * "contract" returned two identical rows with nothing to choose between them.
 * The replacement is the settings page's own `metadata.title`, not a new name.
 */
const SETTINGS_LABEL_OVERRIDES: Record<string, { label: string; keywords: string[] }> = {
  "settings-contracts": {
    label: "Contract clauses",
    keywords: ["clause", "template", "standard terms", "boilerplate", "contract settings"],
  },
};

/** Applies a {@link SETTINGS_LABEL_OVERRIDES} entry, if the item has one. */
const disambiguate = (item: PaletteItem): PaletteItem => {
  const override = SETTINGS_LABEL_OVERRIDES[item.id];
  if (!override) return item;
  return { ...item, label: override.label, keywords: [...(item.keywords ?? []), ...override.keywords] };
};

/**
 * Every destination the palette can reach, derived from the same config the
 * sidebar and the registration page already read.
 *
 * Derived rather than retyped so the two cannot drift: adding a sidebar item
 * makes it searchable for free, and only its synonyms are hand-written. Same
 * reason `buildRegistrationDrillDownChildren` reads the page's own tab config.
 *
 * @returns The static destination registry, in group order.
 */
export function buildDestinationItems(): PaletteItem[] {
  const eventItems = [
    fromNavItem(homeNavItem, "event", "event"),
    // "Details" alone reads as "whose details?" in a flat list: the first
    // reviewer guessed it meant her own profile. The sidebar's heading answers
    // that question, the palette has to answer it in the row.
    { ...fromNavItem(eventDetailsNavItem, "event", "event"), label: "Event details" },
    // Relabelled: the sidebar can call both this and the org item "Users" because
    // they sit in different visual sections, but the palette shows every group at
    // once and two identical rows are unreadable.
    { ...fromNavItem(eventUsersNavItem, "event", "event"), label: "Event access" },
    fromNavItem(notesNavItem, "event", "event"),
    // "Search" reads fine under the sidebar's SOURCE heading, but in a palette
    // that is itself a search box it is ambiguous. Name what it searches.
    ...sourceGroup.items.map((item) =>
      item.id === "search"
        ? { ...fromNavItem(item, "source", "event"), label: "Search hotels" }
        : fromNavItem(item, "source", "event")
    ),
    ...ENTERPRISE_REGISTRATION_TABS.map((tab) => ({
      id: `registration-${tab.id}`,
      // Plain labels: the "Registration" heading carries the context, the same
      // way Settings' heading does for its children.
      label: tab.label,
      group: "registration" as const,
      icon: tab.icon as LucideIcon,
      keywords: [...(KEYWORDS_BY_ID.registration ?? []), ...(KEYWORDS_BY_ID[tab.id] ?? [])],
      scope: "event" as const,
      run: { kind: "navigate" as const, href: registrationTabHref(tab.id) },
      // These rows are built here rather than through `fromNavItem`, so they do
      // not inherit `manageGroup`'s `comingSoon` the way every other gated row
      // does. Read the sidebar's own answer instead of re-deciding it: while
      // Registration is generally available these rows are ungated, and the day
      // that gate becomes a real entitlement check they go disabled here and
      // drop out of the AI chat's link map (ENG-836) without an edit in either
      // place.
      ...(canSeeRegistration() ? {} : { requires: () => false, disabledReason: "Coming soon" }),
    })),
    ...UNDERIVABLE_ITEMS,
  ];

  const orgItems = [
    fromNavItem(eventsNavItem, "org", "org"),
    fromNavItem(chatNavItem, "org", "global"),
    ...organizationItems.flatMap((item) =>
      // Settings' children get their own heading rather than a label prefix.
      item.children?.length
        ? fromNavChildren(item, "settings", "org").map(disambiguate)
        : [fromNavItem(item, "org", "org")]
    ),
    {
      id: "user-groups",
      label: "Groups",
      group: "org" as const,
      icon: Users,
      keywords: KEYWORDS_BY_LEAF.groups,
      scope: "org" as const,
      run: { kind: "navigate" as const, href: "/dashboard/organization/users/groups" },
    },
  ];

  return [...eventItems, ...orgItems, ...reportingItems()];
}

/**
 * Turns the user's accessible events into palette items.
 *
 * No fetch: `useEventOptions()` already holds every accessible event for the
 * active org, uncapped and sorted active-first, because the layout loads them
 * for the sidebar's event switcher. Searching them here costs one map.
 *
 * @param options - The event options from `useEventOptions()`.
 * @returns One palette item per event, matchable by name or by id.
 */
export function buildEventItems(options: { value: string; label: string; bucket?: EventBucket }[]): PaletteItem[] {
  const rank = (bucket: EventBucket) => EVENT_BUCKET_ORDER.indexOf(bucket);

  return [...options]
    .sort((a, b) => rank(a.bucket ?? "active") - rank(b.bucket ?? "active"))
    .map((option) => {
      const bucket = option.bucket ?? "active";
      return {
        id: `event-${option.value}`,
        label: option.label,
        group: "events" as const,
        icon: Calendar,
        // The id is a keyword so "2612" finds the event as readily as its name,
        // which is how ops refer to them.
        keywords: [option.value, `#${option.value}`, bucket === "active" ? "" : bucket].filter(Boolean),
        scope: "global" as const,
        // Same vocabulary the event switcher uses, so a row means the same thing
        // in both places rather than the palette inventing its own words.
        hint: bucket === "active" ? `#${option.value}` : `${BUCKET_LABEL[bucket]} · #${option.value}`,
        bucket,
        eventId: option.value,
        run: { kind: "navigate" as const, href: `/dashboard/home/${option.value}` },
      };
    });
}

/** Icon for the zero-result handoff into AI chat (ENG-769). */
export const CHAT_FALLBACK_ICON = MessageSquare;
